import React, { useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Typography, useTheme, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper as MuiPaper, Stack, MenuItem, Menu, Switch, IconButton, Divider, ButtonBase, Button } from "@mui/material";
import { ComposedChart, CartesianGrid, XAxis, YAxis, Tooltip, Area, Line, Brush, ResponsiveContainer, ReferenceLine, ReferenceArea } from "recharts";
import { addDays, format, getISOWeek, getISOWeekYear, isSameMonth, isSameWeek, subDays, subWeeks } from "date-fns";
import { useForecast, useRunForecast } from "../../hooks/useForecast";
import { APP_CONFIG } from "../../../../config/app.config";
import { Check, MoreVert, ShoppingCart } from "@mui/icons-material";
import { startOfWeek, addWeeks } from "date-fns";
import { addTransparency } from "../../../../application/styles/colorStyles";
import { useProductSales } from "@features/sales/hooks/useSales";
import { Icon } from "@shared/icons";
import { ForecastSourceType } from "@domain/forecast/models/Forecast";

interface TimeTravelGraphProps {
  sourceType: ForecastSourceType; // e.g. 'search'
  source: string;     // e.g. search term value
  connector: string;  // e.g. connector type
  historicalData?: HistoricalData[];
}

interface HistoricalData {
  week: number; year: number;
  value: number;
  anomaly?: boolean;
  detectedAnomaly?: boolean;
}

const TimeTravelGraph: React.FC<TimeTravelGraphProps> = ({ sourceType, source, connector, historicalData }) => {

  const theme = useTheme();

  const { updateWeeklySalesAnomalies, clearWeeklySalesAnomalies, isUpdatingWeeklySalesAnomalies, isClearingWeeklySalesAnomalies } = useProductSales(source);
  const { forecast, isLoading } = useForecast({ sourceType, source, connector });
  const { setShouldUpdate } = useRunForecast(source ?? '');
  const [brushData, setBrushData] = useState<{ startIndex: number; endIndex: number } | null>(null);

  const [showHistoricForecast, setShowHistoricForecast] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const getDateFromYearAndWeek = (year: number, week: number) => {
    if (year === undefined || week === undefined) {
      return new Date();
    }

    const firstWeek = startOfWeek(new Date(year, 0, 4), { weekStartsOn: 1 });
    return addWeeks(firstWeek, week - 1);
  }

  const handleOutOfStock = () => {
    updateWeeklySalesAnomalies(selectedData.map(item => ({ year: item.year, week: item.week, anomaly: true })));
    setShouldUpdate(true);
  }

  const handleInStock = () => {
    updateWeeklySalesAnomalies(selectedData.map(item => ({ year: item.year, week: item.week, anomaly: false })));
    setShouldUpdate(true);
  }

  const handleAutodetect = () => {
    clearWeeklySalesAnomalies(selectedData.map(item => ({ year: item.year, week: item.week })));
    setShouldUpdate(true);
  }

  const chartData = useMemo(() => {
    const weekly = forecast?.weeklyForecasts || [];
    const forecastData = weekly
      .slice()
      .sort((a, b) => (a.year === b.year ? a.week - b.week : a.year - b.year))
      .map(w => ({
        year: w.year,
        week: w.week,
        date: getDateFromYearAndWeek(w.year, w.week),
        forecast: w.forecastQuantity,
        forecastMin: w.forecastQuantityMin,
        forecastMax: w.forecastQuantityMax,
        forecastBands: [w.forecastQuantityMin, w.forecastQuantityMax],
        historical: undefined as number | undefined,
        anomaly: undefined as boolean | undefined,
        detectedAnomaly: undefined as boolean | undefined
      }));

    // Merge historical data into forecast data for better visualization and analytics
    if (historicalData && historicalData.length > 0) {
      // 1. Build a map of historical data keyed by date in 'yyyy-MM-dd' format
      const historicalMap = new Map<string, { value: number; anomaly?: boolean, detectedAnomaly?: boolean }>();

      for (const item of historicalData) {
        const dateKey = `${item.year}-W${String(item.week).padStart(2, '0')}`;

        if (historicalMap.has(dateKey)) {
          const existing = historicalMap.get(dateKey)!;
          historicalMap.set(dateKey, {
            value: existing.value + item.value,
            anomaly: existing.anomaly || item.anomaly,
            detectedAnomaly: existing.detectedAnomaly || item.detectedAnomaly
          });
        } else {
          historicalMap.set(dateKey, { value: item.value, anomaly: item.anomaly, detectedAnomaly: item.detectedAnomaly });
        }
      }

      // 2. Enrich the forecast data with corresponding historical values and anomaly flags
      forecastData.forEach((data) => {
        const historical = historicalMap.get(`${data.year}-W${String(data.week).padStart(2, '0')}`);
        if (historical) {
          data.historical = historical.value;
          data.anomaly = historical.anomaly;
          data.detectedAnomaly = historical.detectedAnomaly;
        }
      });

      // 3. Add missing historical data points that are not present in forecastData
      for (const item of historicalData) {
        const dateKey = `${item.year}-W${item.week}`;
        const alreadyExists = forecastData.some(d => `${d.year}-W${d.week}` === dateKey);
        if (!alreadyExists) {
          forecastData.push({
            year: item.year,
            week: item.week,
            date: getDateFromYearAndWeek(item.year, item.week),
            forecast: undefined,
            forecastMin: undefined,
            forecastMax: undefined,
            forecastBands: [undefined, undefined],
            historical: item.value,
            anomaly: item.anomaly,
            detectedAnomaly: item.detectedAnomaly
          });
        }
      }

      // 4. For each forecastData point, fill in the historical value from exactly 12 months ago (if exists)
      //    Assumes forecastData is sorted after this step
      forecastData.sort((a, b) => a.year - b.year || a.week - b.week);

      for (let i = 0; i < forecastData.length; i++) {
        // Calculate the date 12 months (52 weeks) before the current date
        const yearWeek = forecastData[i].year * 100 + forecastData[i].week;
        const currentYearWeek = getISOWeekYear(new Date()) * 100 + getISOWeek(new Date());

        if (!showHistoricForecast && yearWeek < currentYearWeek) {
          forecastData[i].forecast = undefined;
          forecastData[i].forecastMin = undefined;
          forecastData[i].forecastMax = undefined;
          forecastData[i].forecastBands = [undefined, undefined];
        }

        if (yearWeek > currentYearWeek) {
          forecastData[i].historical = undefined;
        }
      }
    }

    return forecastData;
  }, [forecast, historicalData, showHistoricForecast]);

  const anomalySegments = useMemo(() => {
    const segments: Array<{ startIndex: number; endIndex: number }> = [];
    let currentSegment: { startIndex: number; endIndex: number } | null = null;

    chartData.forEach((data, index) => {
      if (data.anomaly) {
        if (currentSegment === null) {
          // Start a new segment
          currentSegment = { startIndex: index, endIndex: index };
        } else {
          // Extend the current segment
          currentSegment.endIndex = index;
        }
      } else {
        if (currentSegment !== null) {
          // End the current segment
          segments.push(currentSegment);
          currentSegment = null;
        }
      }
    });

    // Don't forget the last segment if it extends to the end
    if (currentSegment !== null) {
      segments.push(currentSegment);
    }

    return segments;
  }, [chartData]);

  const showCurrentWeekLine = useMemo(() => {
    if (!chartData.length) {
      return false;
    }
    return chartData.some((entry) => isSameWeek(entry.date, new Date()));
  }, [chartData]);

  const setBrush = (startIndex?: number, endIndex?: number) => {
    const startBrushWidth = 18;

    // Find the index of the data point that matches (or is closest to) the current week
    const now = new Date();
    let currentIndex = chartData.findIndex(entry => isSameWeek(entry.date, now));
    if (currentIndex === -1) {
      // If current week is not present, default to the last point
      currentIndex = chartData.length - 1;
    }

    if (startIndex === undefined || endIndex === undefined) {
      const brushWidth = Math.min(startBrushWidth, chartData.length);
      // Center the brush around the current week index
      startIndex = Math.max(0, Math.min(chartData.length - brushWidth, currentIndex - Math.floor(brushWidth / 2)));
      endIndex = Math.min(chartData.length - 1, startIndex + brushWidth - 1);
    }


    setBrushData(prev => {
      if (prev && prev.startIndex === startIndex && prev.endIndex === endIndex) {
        return prev;
      }
      return { startIndex, endIndex };
    });
  }

  useMemo(() => {
    if (!chartData || chartData.length === 0) {
      setBrushData(null);
      return;
    }

    if (brushData === null) {
      setBrush();
    }

  }, [chartData]);

  const isAnomaly = (row: { anomaly?: boolean, detectedAnomaly?: boolean }) => {
    if (row.anomaly !== null) {
      return row.anomaly;
    }

    if (row.detectedAnomaly !== null) {
      return row.detectedAnomaly;
    }
    return false;
  }

  const displayData = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return [];
    }
    return chartData.slice(brushData ? brushData.startIndex : 0, brushData ? brushData.endIndex + 1 : chartData.length);
  }, [chartData, brushData]);


  const [selectedData, setSelectedData] = useState<{ year: number; week: number }[]>([]);

  const toggleSelect = (row: { year: number; week: number }, event?: React.MouseEvent<HTMLTableCellElement>) => {
    event?.preventDefault();

    setSelectedData(prev => {
      const alreadySelected = prev.some(item => item.year === row.year && item.week === row.week);

      // Helper to find the row index in displayData
      const getRowIndex = (target: { year: number; week: number }) =>
        displayData.findIndex(item => item.year === target.year && item.week === target.week);

      if (event && (event.ctrlKey || event.metaKey)) {
        // Ctrl/Cmd: Add or keep selection
        if (!alreadySelected) {
          return [...prev, row];
        }
        return prev;
      } else if (event && event.shiftKey) {
        // Shift: Select a contiguous range from the last selected to the clicked one
        if (displayData.length === 0) {
          return [row];
        }
        let anchor: { year: number; week: number } | null = null;
        if (prev.length > 0) {
          anchor = prev[prev.length - 1];
        }
        // If nothing was previously selected, just select this one
        if (!anchor) {
          return [row];
        }
        const anchorIdx = getRowIndex(anchor);
        const rowIdx = getRowIndex(row);
        if (anchorIdx === -1 || rowIdx === -1) {
          return [row];
        }
        // Compute range
        const [start, end] = anchorIdx < rowIdx ? [anchorIdx, rowIdx] : [rowIdx, anchorIdx];
        const range = displayData.slice(start, end + 1);
        // Merge with previous selection, preserving unique selection
        const merged = [...prev];
        range.forEach(item => {
          if (!merged.some(sel => sel.year === item.year && sel.week === item.week)) {
            merged.push(item);
          }
        });
        return merged;
      } else {
        // Default: Replace selection with this row, or reset if already selected
        if (!alreadySelected) {
          return [row];
        }
        return [];
      }
    });
  }

  const chartHeight = 400;
  const tableContainerRef = useRef<HTMLDivElement>(null);


  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={300}>
        <CircularProgress />
      </Box>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={300}>
        <Typography color="text.secondary">No forecast data</Typography>
      </Box>
    );
  }

  return (
    <Box>

      <Stack direction="row" flex={1} justifyContent="space-between" alignItems="center" spacing={2} pb={4}>
        <Typography variant="subtitle2" color="text.secondary">Forecast</Typography>

        <IconButton size="small"
          onClick={(event) => setAnchorEl(event.currentTarget)}
        ><MoreVert /></IconButton>

        <Menu open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
          <MenuItem> Show Historic Forecast <Switch checked={showHistoricForecast} onChange={(event) => setShowHistoricForecast(event.target.checked)} /> </MenuItem>
        </Menu>
      </Stack>


      <Box
        sx={{
          position: "relative",
          height: chartHeight,
          width: "100%",
        }}
      >
        <ResponsiveContainer width="100%" height={chartHeight}>
          <ComposedChart
            data={chartData}
            margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
          >

            <Brush
              dataKey="date"
              height={15}
              tickFormatter={(value: Date) => {
                return "";
              }}
              stroke={theme.palette.primary.main}
              startIndex={brushData ? brushData.startIndex : undefined}
              endIndex={brushData ? brushData.endIndex : undefined}
              onChange={(data) => {
                if (
                  data &&
                  typeof data.startIndex === "number" &&
                  typeof data.endIndex === "number"
                ) {
                  setBrush(data.startIndex, data.endIndex);
                }
              }}
            />

            <CartesianGrid strokeDasharray="0 0" stroke={APP_CONFIG.ui.theme.colors.border} vertical={false} />
            <XAxis
              dataKey="date"
              fontFamily={theme.typography.fontFamily}
              fontSize={12}
              height={50}
              tick={<CustomTick />}
              interval={brushData ? Math.round(((brushData.endIndex - brushData.startIndex) / chartData.length * 5)) : 0}
              padding={{ left: ((tableContainerRef.current?.clientWidth ?? 0) / displayData.length) / 2, right: ((tableContainerRef.current?.clientWidth ?? 0) / displayData.length) / 2 }}
            />

            <XAxis
              dataKey="week"
              fontFamily={theme.typography.fontFamily}
              fontSize={12}
              height={50}
              interval={0}
              hide
              xAxisId="week"
              padding={{ left: 30, right: 30 }}
            />


            <YAxis
              width={60}
              tickLine={false}
              axisLine={false}
              fontFamily={theme.typography.fontFamily}
              fontSize={12}
              yAxisId="right"
              orientation="right"
              allowDataOverflow={true}
            />

            <Tooltip />

            {forecast && (
              <>
                <Area
                  yAxisId="right"
                  type="natural"
                  dataKey="forecastBands"
                  name="Forecast Range"
                  stroke="none"
                  fill={theme.palette.primary.main}
                  fillOpacity={0.1}
                  isAnimationActive={false}
                />

                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="forecast"
                  name="Forecast"
                  stroke={theme.palette.primary.main}
                  strokeWidth={5}
                  dot={false}
                  isAnimationActive={false}
                />
              </>
            )}


            {historicalData && historicalData.length > 0 && (
              <>
                {anomalySegments.map((segment, index) => (
                  <ReferenceArea
                    key={`anomaly-${index}`}
                    yAxisId="right"
                    x1={segment.startIndex}
                    x2={segment.endIndex}
                    fill={addTransparency(theme.palette.error.main, 0.2)}
                    fillOpacity={1}
                  />
                ))}
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="historical"
                  name="Historical Data"
                  stroke={theme.palette.secondary.main}
                  strokeWidth={5}
                  dot={false}
                  isAnimationActive={false}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="historical12MonthsAgo"
                  name="Historical Data (12 Months Ago)"
                  stroke={theme.palette.grey[600]}
                  strokeWidth={1}
                  strokeDasharray="5 5"
                  dot={false}
                  isAnimationActive={false}
                  opacity={0.7}
                />
              </>
            )}


            {showCurrentWeekLine && (
              <ReferenceLine
                yAxisId="right"
                xAxisId="week"
                x={getISOWeek(new Date())}
                stroke={theme.palette.secondary.main}
                strokeWidth={1}
              />
            )}

          </ComposedChart>
        </ResponsiveContainer>
      </Box>

      <Box sx={{ marginRight: '60px', marginTop: "8px" }}>
        <TableContainer ref={tableContainerRef}>
          <Table sx={{ borderCollapse: 'separate' }}>
            <TableBody>
              <TableRow sx={{ border: '1px solid divider' }}>
                {displayData.map((row, idx) => (
                  <TableCell
                    align="center"
                    colSpan={1}
                    onClick={sourceType === ForecastSourceType.PRODUCT ? (event: React.MouseEvent<HTMLTableCellElement>) => toggleSelect(row, event) : undefined}
                    key={row.year + '-' + row.week}
                    sx={{
                      borderCollapse: 'separate',
                      px: 0,
                      cursor: sourceType === ForecastSourceType.PRODUCT ? 'pointer' : 'default',
                      width: `${100 / displayData.length}%`,
                      maxWidth: `${100 / displayData.length}%`,
                      minWidth: `${100 / displayData.length}%`,
                      border: '1px solid',
                      borderColor: selectedData.some(item => item.year === row.year && item.week === row.week)
                        ? theme.palette.primary.main
                        : theme.palette.divider,
                      backgroundColor: isAnomaly(row)
                        ? selectedData.some(item => item.year === row.year && item.week === row.week)
                          ? addTransparency(theme.palette.error.main, 0.4)
                          : addTransparency(theme.palette.error.main, 0.2)
                        : selectedData.some(item => item.year === row.year && item.week === row.week)
                          ? addTransparency(theme.palette.primary.main, 0.2)
                          : 'transparent',
                    }}
                  >
                    <>
                      {historicalData && historicalData.length > 0 &&
                        displayData.length < (tableContainerRef.current?.clientWidth ?? 0) / 30 && (
                          <>
                            {row.forecast !== undefined ? (
                              <Typography variant="body2" color="primary.main" sx={{ userSelect: 'none' }}>
                                {row.forecast}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ userSelect: 'none' }}>
                                {row.historical ?? 0}
                              </Typography>
                            )}
                          </>
                        )}
                    </>
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>

        </TableContainer>

        {selectedData.length > 0 && (
          <Stack direction="row" spacing={2}
            color="white"
            alignItems="center"
            px={2}
            py={1}
            sx={{
              marginTop: "8px",
              borderRadius: 1,
              boxShadow: 1,
              backgroundColor: theme.palette.secondary.main,
            }}

          >

            <Typography variant="body2" color="white">Edit {selectedData.length} week{selectedData.length > 1 ? 's' : ''}</Typography>

            <Stack direction="row" spacing={2} flex={1} alignItems="center">
              <Divider orientation="vertical" sx={{ height: "16px", width: "2px" }} />

              <Button onClick={handleOutOfStock} startIcon={<Icon icon="PackageX" />} variant="text" color="inherit" size="small">
                Out of stock
              </Button>

              <Button onClick={handleInStock} startIcon={<Icon icon="PackagePlus" />} variant="text" color="inherit" size="small">
                In stock
              </Button>

              <Button onClick={handleAutodetect} startIcon={<Icon icon="PackageCheck" />} variant="text" color="inherit" size="small">
                Autodetect
              </Button>

              {isUpdatingWeeklySalesAnomalies || isClearingWeeklySalesAnomalies ? <CircularProgress color="inherit" size={16} /> : null}

            </Stack>

            <Button variant="text" color="inherit" size="small" onClick={() => setSelectedData([])}>Clear selection</Button>
          </Stack>
        )}
      </Box>


    </Box >
  );
};

const CustomTick = ({ x, y, payload }: any) => {

  const theme = useTheme();

  const date = payload.value instanceof Date ? payload.value : new Date(payload.value);

  return (
    <text
      x={x}
      y={y + 10}
      textAnchor="middle"
      fontFamily={theme.typography.fontFamily}
      fontSize={12}
      fill={theme.palette.text.secondary}
    >
      <tspan
        x={x}
        dy={0}
        fontSize={12}
        fill={theme.palette.text.secondary}
      >
        {format(date, "MMM d")}
      </tspan>

      {getISOWeekYear(date) !== getISOWeekYear(subWeeks(date, 1)) ? (
        <tspan
          x={x}
          dy={14}
          fontSize={10}
          fill={theme.palette.text.secondary}
        >
          {format(date, "yyyy")}
        </tspan>
      ) : (
        <tspan
          x={x}
          dy={14}
          fontSize={10}
          fill={theme.palette.text.secondary}
        >
          Week {getISOWeek(date)}
        </tspan>
      )}
    </text>
  );
};

export default TimeTravelGraph;
